/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Modal, Input, Button, message, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { ModalProps } from 'antd/lib/modal';
import { queryCache, useMutation } from 'react-query';
import { addCustomMovement } from '~src/services/api/movements';
import { useIntl } from 'react-intl';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';

interface AddCustomMovementParams extends ModalProps {
  projectId: number | undefined;
  tenderId: number | string;
}

const initialFormsValues = {
  note: '',
  field: '',
  old_value: '',
  new_value: '',
};

export const AddCustomMovement: React.FC<AddCustomMovementParams> = ({
  projectId,
  tenderId,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const [formValues, setFormValues] = useState(initialFormsValues);

  const [doAddCustomMovement, { isLoading }] = useMutation(addCustomMovement, {
    onSuccess: () => {
      queryCache.invalidateQueries('movements');
      message.success(intl.formatMessage({ id: 'success' }));
    },
    onError: () => {
      message.error('Failed');
    },
  });

  const isValid = () => {
    if (!formValues.note) return false;
    if (!formValues.field) return false;
    if (!formValues.old_value) return false;
    if (!formValues.new_value) return false;

    return true;
  };

  if (!projectId && !tenderId) return null;

  const handleSubmit = async () => {
    await doAddCustomMovement({
      project_id: projectId,
      tender_id: Number(tenderId),
      body: formValues,
    });

    setFormValues(initialFormsValues);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
          <span>{intl.formatMessage({ id: 'add-custom-movement' })}</span>
          <IconPopup
            headerId={'add-custom-movement'}
            messageId={'add-custom-movement-text'}
          />
        </div>
      }
      width="min(90vw, 400px)"
      bodyStyle={{ padding: '20px' }}
      centered
      destroyOnClose
      maskClosable={true}
      onCancel={onCancel}
      footer={null}
      {...modalProps}>
      <div
        style={{
          padding: '10px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}>
        <div
          css={{
            width: '90%',
            gap: '10px',
            display: 'flex',
            flexFlow: 'column nowrap',
          }}>
          <div>
            <label>
              {intl.formatMessage({ id: 'note' })}
              <span
                style={{
                  color: '#ff4d4f',
                  fontSize: '11px',
                  position: 'relative',
                  top: '-2px',
                  marginLeft: '4px',
                  fontWeight: 'normal',
                }}>
                *
                {intl.formatMessage({
                  id: 'required',
                })}
              </span>
            </label>
            <Input
              placeholder={intl.formatMessage({ id: 'note' })}
              value={formValues.note}
              onChange={(e) => {
                setFormValues({ ...formValues, note: e.target.value });
              }}
              allowClear
            />
          </div>
          <div>
            <label>
              {intl.formatMessage({ id: 'field' })}
              <span
                style={{
                  color: '#ff4d4f',
                  fontSize: '11px',
                  position: 'relative',
                  top: '-2px',
                  marginLeft: '4px',
                  fontWeight: 'normal',
                }}>
                *
                {intl.formatMessage({
                  id: 'required',
                })}
              </span>
            </label>
            <Input
              placeholder={intl.formatMessage({ id: 'field' })}
              value={formValues.field}
              onChange={(e) => {
                setFormValues({ ...formValues, field: e.target.value });
              }}
              allowClear
            />
          </div>
          <div>
            <label>
              {intl.formatMessage({ id: 'old-value' })}
              <span
                style={{
                  color: '#ff4d4f',
                  fontSize: '11px',
                  position: 'relative',
                  top: '-2px',
                  marginLeft: '4px',
                  fontWeight: 'normal',
                }}>
                *
                {intl.formatMessage({
                  id: 'required',
                })}
              </span>
            </label>
            <Input
              placeholder={intl.formatMessage({ id: 'old-value' })}
              value={formValues.old_value}
              onChange={(e) => {
                setFormValues({ ...formValues, old_value: e.target.value });
              }}
              allowClear
            />
          </div>
          <div>
            <label>
              {intl.formatMessage({ id: 'new-value' })}
              <span
                style={{
                  color: '#ff4d4f',
                  fontSize: '11px',
                  position: 'relative',
                  top: '-2px',
                  marginLeft: '4px',
                  fontWeight: 'normal',
                }}>
                *
                {intl.formatMessage({
                  id: 'required',
                })}
              </span>
            </label>
            <Input
              placeholder={intl.formatMessage({ id: 'new-value' })}
              value={formValues.new_value}
              onChange={(e) => {
                setFormValues({ ...formValues, new_value: e.target.value });
              }}
              allowClear
            />
          </div>
          <div
            css={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              marginTop: '25px',
              gap: '10px',
            }}>
            <Button type="default" onClick={onCancel} disabled={isLoading}>
              {intl.formatMessage({ id: 'close' })}
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!isValid()}
              loading={isLoading}>
              {intl.formatMessage({ id: 'save' })}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
